
import { supabase, Patient, Prescription, ClinicUser } from './supabaseClient';

export class SupabaseStorage {
  // Patient operations
  async getPatients(): Promise<Patient[]> {
    const { data, error } = await supabase
      .from('patients')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching patients:', error);
      throw error;
    }
    
    return data.map(patient => ({
      id: patient.id,
      firstName: patient.first_name,
      lastName: patient.last_name,
      email: patient.email,
      phone: patient.phone,
      dateOfBirth: patient.date_of_birth,
      gender: patient.gender,
      address: patient.address,
      bloodType: patient.blood_type,
      allergies: patient.allergies,
      emergencyContact: patient.emergency_contact,
      insuranceProvider: patient.insurance_provider,
      insuranceNumber: patient.insurance_number,
      lastVisit: patient.last_visit,
      status: patient.status
    }));
  }

  async addPatient(patient: Omit<Patient, 'id'>): Promise<Patient> {
    const { data, error } = await supabase
      .from('patients')
      .insert({
        first_name: patient.firstName,
        last_name: patient.lastName,
        email: patient.email,
        phone: patient.phone,
        date_of_birth: patient.dateOfBirth,
        gender: patient.gender,
        address: patient.address,
        blood_type: patient.bloodType,
        allergies: patient.allergies,
        emergency_contact: patient.emergencyContact,
        insurance_provider: patient.insuranceProvider,
        insurance_number: patient.insuranceNumber,
        last_visit: patient.lastVisit,
        status: patient.status
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding patient:', error);
      throw error;
    }

    return {
      id: data.id,
      firstName: data.first_name,
      lastName: data.last_name,
      email: data.email,
      phone: data.phone,
      dateOfBirth: data.date_of_birth,
      gender: data.gender,
      address: data.address,
      bloodType: data.blood_type,
      allergies: data.allergies,
      emergencyContact: data.emergency_contact,
      insuranceProvider: data.insurance_provider,
      insuranceNumber: data.insurance_number,
      lastVisit: data.last_visit,
      status: data.status
    };
  }

  async updatePatient(patientId: number, updates: Partial<Patient>): Promise<Patient | null> {
    const updateData: any = {};
    
    if (updates.firstName) updateData.first_name = updates.firstName;
    if (updates.lastName) updateData.last_name = updates.lastName;
    if (updates.email) updateData.email = updates.email;
    if (updates.phone) updateData.phone = updates.phone;
    if (updates.dateOfBirth) updateData.date_of_birth = updates.dateOfBirth;
    if (updates.gender) updateData.gender = updates.gender;
    if (updates.address) updateData.address = updates.address;
    if (updates.bloodType) updateData.blood_type = updates.bloodType;
    if (updates.allergies) updateData.allergies = updates.allergies;
    if (updates.emergencyContact) updateData.emergency_contact = updates.emergencyContact;
    if (updates.insuranceProvider) updateData.insurance_provider = updates.insuranceProvider;
    if (updates.insuranceNumber) updateData.insurance_number = updates.insuranceNumber;
    if (updates.lastVisit) updateData.last_visit = updates.lastVisit;
    if (updates.status) updateData.status = updates.status;

    const { data, error } = await supabase
      .from('patients')
      .update(updateData)
      .eq('id', patientId)
      .select()
      .single();

    if (error) {
      console.error('Error updating patient:', error);
      throw error;
    }

    if (!data) return null;

    return {
      id: data.id,
      firstName: data.first_name,
      lastName: data.last_name,
      email: data.email,
      phone: data.phone,
      dateOfBirth: data.date_of_birth,
      gender: data.gender,
      address: data.address,
      bloodType: data.blood_type,
      allergies: data.allergies,
      emergencyContact: data.emergency_contact,
      insuranceProvider: data.insurance_provider,
      insuranceNumber: data.insurance_number,
      lastVisit: data.last_visit,
      status: data.status
    };
  }

  async deletePatient(patientId: number): Promise<boolean> {
    const { error } = await supabase
      .from('patients')
      .delete()
      .eq('id', patientId);

    if (error) {
      console.error('Error deleting patient:', error);
      throw error;
    }

    return true;
  }

  // Prescription operations
  async getPrescriptions(): Promise<Prescription[]> {
    const { data, error } = await supabase
      .from('prescriptions')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching prescriptions:', error);
      throw error;
    }
    
    return data.map(prescription => ({
      id: prescription.id,
      patientId: prescription.patient_id,
      doctorName: prescription.doctor_name,
      medications: prescription.medications,
      diagnosis: prescription.diagnosis,
      notes: prescription.notes,
      date: prescription.date,
      status: prescription.status
    }));
  }

  async addPrescription(prescription: Omit<Prescription, 'id'>): Promise<Prescription> {
    const { data, error } = await supabase
      .from('prescriptions')
      .insert({
        patient_id: prescription.patientId,
        doctor_name: prescription.doctorName,
        medications: prescription.medications,
        diagnosis: prescription.diagnosis,
        notes: prescription.notes,
        date: prescription.date,
        status: prescription.status
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding prescription:', error);
      throw error;
    }

    return {
      id: data.id,
      patientId: data.patient_id,
      doctorName: data.doctor_name,
      medications: data.medications,
      diagnosis: data.diagnosis,
      notes: data.notes,
      date: data.date,
      status: data.status
    };
  }

  async updatePrescription(prescriptionId: number, updates: Partial<Prescription>): Promise<Prescription | null> {
    const updateData: any = {};
    
    if (updates.patientId) updateData.patient_id = updates.patientId;
    if (updates.doctorName) updateData.doctor_name = updates.doctorName;
    if (updates.medications) updateData.medications = updates.medications;
    if (updates.diagnosis) updateData.diagnosis = updates.diagnosis;
    if (updates.notes) updateData.notes = updates.notes;
    if (updates.date) updateData.date = updates.date;
    if (updates.status) updateData.status = updates.status;

    const { data, error } = await supabase
      .from('prescriptions')
      .update(updateData)
      .eq('id', prescriptionId)
      .select()
      .single();

    if (error) {
      console.error('Error updating prescription:', error);
      throw error;
    }

    if (!data) return null;

    return {
      id: data.id,
      patientId: data.patient_id,
      doctorName: data.doctor_name,
      medications: data.medications,
      diagnosis: data.diagnosis,
      notes: data.notes,
      date: data.date,
      status: data.status
    };
  }

  async deletePrescription(prescriptionId: number): Promise<boolean> {
    const { error } = await supabase
      .from('prescriptions')
      .delete()
      .eq('id', prescriptionId);

    if (error) {
      console.error('Error deleting prescription:', error);
      throw error;
    }

    return true;
  }

  // User authentication operations
  async authenticateUser(username: string, password: string): Promise<ClinicUser | null> {
    try {
      console.log('Authenticating user:', username);
      
      const { data, error } = await supabase
        .from('clinic_users')
        .select('*')
        .or(`username.eq.${username},email.eq.${username}`)
        .eq('password_hash', password)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        console.error('Authentication failed:', error);
        return null;
      }

      // Update last login
      await supabase
        .from('clinic_users')
        .update({ last_login: new Date().toISOString() })
        .eq('id', data.id);

      return {
        id: data.id,
        username: data.username,
        email: data.email,
        password: data.password_hash,
        role: data.role,
        permissions: data.permissions,
        firstName: data.first_name,
        lastName: data.last_name,
        phone: data.phone,
        isActive: data.is_active,
        lastLogin: data.last_login,
        createdAt: data.created_at
      };
    } catch (error) {
      console.error('Error in authenticateUser:', error);
      throw error;
    }
  }

  // User management operations
  async getUsers(): Promise<ClinicUser[]> {
    try {
      console.log('Fetching users from Supabase...');
      
      const { data, error } = await supabase
        .from('clinic_users')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching users from Supabase:', error);
        throw error;
      }
      
      console.log('Successfully fetched users:', data?.length || 0);
      
      return data.map(user => ({
        id: user.id,
        username: user.username,
        email: user.email,
        password: user.password_hash,
        role: user.role,
        permissions: user.permissions,
        firstName: user.first_name,
        lastName: user.last_name,
        phone: user.phone,
        isActive: user.is_active,
        lastLogin: user.last_login,
        createdAt: user.created_at
      }));
    } catch (error) {
      console.error('Error in getUsers:', error);
      throw error;
    }
  }

  async addUser(userData: Omit<ClinicUser, 'id' | 'createdAt'>): Promise<ClinicUser> {
    try {
      console.log('Adding user to Supabase:', userData.username);
      
      const { data, error } = await supabase
        .from('clinic_users')
        .insert({
          username: userData.username,
          email: userData.email,
          password_hash: userData.password,
          role: userData.role,
          permissions: userData.permissions,
          first_name: userData.firstName,
          last_name: userData.lastName,
          phone: userData.phone,
          is_active: userData.isActive || true
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding user to Supabase:', error);
        throw error;
      }

      console.log('Successfully added user to Supabase:', data.id);

      return {
        id: data.id,
        username: data.username,
        email: data.email,
        password: data.password_hash,
        role: data.role,
        permissions: data.permissions,
        firstName: data.first_name,
        lastName: data.last_name,
        phone: data.phone,
        isActive: data.is_active,
        lastLogin: data.last_login,
        createdAt: data.created_at
      };
    } catch (error) {
      console.error('Error in addUser:', error);
      throw error;
    }
  }

  async updateUser(userId: number, updates: Partial<ClinicUser>): Promise<ClinicUser | null> {
    try {
      console.log('Updating user in Supabase:', { userId, updates });
      
      const updateData: any = {};
      
      if (updates.username) updateData.username = updates.username;
      if (updates.email) updateData.email = updates.email;
      if (updates.password) updateData.password_hash = updates.password;
      if (updates.role) updateData.role = updates.role;
      if (updates.permissions) updateData.permissions = updates.permissions;
      if (updates.firstName) updateData.first_name = updates.firstName;
      if (updates.lastName) updateData.last_name = updates.lastName;
      if (updates.phone) updateData.phone = updates.phone;
      if (updates.isActive !== undefined) updateData.is_active = updates.isActive;

      console.log('Supabase update data:', updateData);

      const { data, error } = await supabase
        .from('clinic_users')
        .update(updateData)
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        console.error('Error updating user in Supabase:', error);
        throw error;
      }

      if (!data) {
        console.error('No data returned from Supabase update');
        return null;
      }

      const updatedUser = {
        id: data.id,
        username: data.username,
        email: data.email,
        password: data.password_hash,
        role: data.role,
        permissions: data.permissions,
        firstName: data.first_name,
        lastName: data.last_name,
        phone: data.phone,
        isActive: data.is_active,
        lastLogin: data.last_login,
        createdAt: data.created_at
      };

      console.log('Successfully updated user in Supabase:', updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('Error in updateUser:', error);
      throw error;
    }
  }

  async deleteUser(userId: number): Promise<boolean> {
    try {
      console.log('Deleting user from Supabase:', userId);
      
      const { error } = await supabase
        .from('clinic_users')
        .delete()
        .eq('id', userId);

      if (error) {
        console.error('Error deleting user from Supabase:', error);
        throw error;
      }

      console.log('Successfully deleted user from Supabase:', userId);
      return true;
    } catch (error) {
      console.error('Error in deleteUser:', error);
      throw error;
    }
  }

  // Export data functionality
  async exportDataToJSON(): Promise<void> {
    try {
      const [patients, prescriptions] = await Promise.all([
        this.getPatients(),
        this.getPrescriptions()
      ]);

      const data = {
        patients,
        prescriptions,
        lastUpdated: new Date().toISOString(),
        version: '2.0'
      };

      const jsonString = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `homeopathic_clinic_data_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
      throw error;
    }
  }

  // Get statistics
  async getStatistics() {
    try {
      const [patientsResult, prescriptionsResult] = await Promise.all([
        supabase.from('patients').select('id, status'),
        supabase.from('prescriptions').select('id')
      ]);

      const patients = patientsResult.data || [];
      const prescriptions = prescriptionsResult.data || [];

      return {
        totalPatients: patients.length,
        activePatients: patients.filter(p => p.status === 'Active').length,
        totalPrescriptions: prescriptions.length,
        lastUpdated: new Date().toISOString(),
        version: '2.0'
      };
    } catch (error) {
      console.error('Error getting statistics:', error);
      return {
        totalPatients: 0,
        activePatients: 0,
        totalPrescriptions: 0,
        lastUpdated: new Date().toISOString(),
        version: '2.0'
      };
    }
  }
}

// Create singleton instance
export const supabaseStorage = new SupabaseStorage();
